// YTAppendQnaModal — After Approve, offers to append this Q&A to a silo
// file that already references the source YouTube video. Randy Feb 2026:
//   - Q4c: single-select (radio, not checkbox) so the creator picks the
//     ONE file the Q&A logically belongs to.
//   - Q4b: if no matches, just show a "skip" state — no create-new option.
//   - Q4d: do NOT create a stub file if the video URL isn't in the silo yet.
// Feb 2026 (Randy pt-yt-append-any-file):
//   - When the auto-match set is wrong/absent, expose an "Or append to any
//     file in this silo…" collapsible picker at the bottom. Loads the full
//     silo library from /api/silos/{silo_id}/docs on demand (not upfront —
//     avoids paying the fetch cost when the auto-match was correct).
//   - Same single-select radio, shared with the auto-match list — you can
//     ONLY have one file selected across both lists.
import { useEffect, useState } from "react";
import { X, FileText, Check, CaretDown, CaretUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ytApi } from "@/lib/ytApi";
import { api } from "@/lib/api";

export default function YTAppendQnaModal({ draftId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [matches, setMatches] = useState([]);
  const [videoId, setVideoId] = useState("");
  const [siloId, setSiloId] = useState("");
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  // Feb 2026 (Randy pt-yt-append-any-file) — "any file in this silo" picker.
  // Collapsed by default; opening it lazy-loads the silo library once.
  const [anyOpen, setAnyOpen] = useState(false);
  const [anyLoading, setAnyLoading] = useState(false);
  const [anyDocs, setAnyDocs] = useState([]);

  useEffect(() => {
    ytApi.matchFiles(draftId).then((r) => {
      setMatches(r.matches || []);
      setVideoId(r.video_id || "");
      setSiloId(r.silo_id || "");
      // Auto-select if there's exactly one match
      if ((r.matches || []).length === 1) setSelectedDoc(r.matches[0].doc_id);
    }).catch(() => {
      setMatches([]);
    }).finally(() => setLoading(false));
  }, [draftId]);

  // Lazy-load the full library the first time the "any file" section is opened.
  // The auto-match list is often correct, so we don't want to eat the round-trip
  // upfront. Cached in state — closing + reopening does NOT re-fetch.
  const openAnyPicker = async () => {
    if (anyOpen) { setAnyOpen(false); return; }
    setAnyOpen(true);
    if (anyDocs.length > 0 || !siloId) return;
    setAnyLoading(true);
    try {
      const r = await api.get(`/silos/${siloId}/docs`);
      // Sort by tier ascending then by name — matches the library UX pattern.
      const sorted = [...(r.data || [])].sort((a, b) => {
        const ta = a.tier || 3;
        const tb = b.tier || 3;
        if (ta !== tb) return ta - tb;
        const na = (a.title || a.filename || a.name || "").toLowerCase();
        const nb = (b.title || b.filename || b.name || "").toLowerCase();
        return na.localeCompare(nb);
      });
      setAnyDocs(sorted);
    } catch {
      toast.error("Couldn't load the silo library.");
      setAnyDocs([]);
    } finally {
      setAnyLoading(false);
    }
  };

  const doAppend = async () => {
    if (!selectedDoc) return toast.error("Pick a file first.");
    setBusy(true);
    try {
      await ytApi.appendQnaToFile(draftId, selectedDoc);
      toast.success("Q&A appended to file.");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Append failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[220] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="yt-append-modal" onClick={onClose}>
      <div className="w-full max-w-lg bg-[#FDFCF8] border hairline rounded-xl shadow-lg max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between px-4 py-3 border-b hairline">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68] font-medium">After posting</div>
            <div className="text-[14px] font-medium text-[#1C1B1A]">Save this Q&amp;A to your silo?</div>
            <div className="text-[11px] text-[#6B6A68] mt-0.5">Adds it to a file that already references this video, so the AI learns from your best answers.</div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#F0F1EE]" data-testid="yt-append-close">
            <X size={16} />
          </button>
        </div>

        <div className="p-4 overflow-y-auto flex-1">
          {loading && <div className="text-center py-6 text-[12px] text-[#6B6A68]">Scanning silo files…</div>}

          {!loading && matches.length === 0 && (
            <div className="text-center py-6 space-y-1.5" data-testid="yt-append-no-matches">
              <FileText size={26} weight="duotone" className="text-[#6B6A68]/50 mx-auto" />
              <div className="text-[13px] font-medium text-[#1C1B1A]">No matching files found.</div>
              <div className="text-[11.5px] text-[#6B6A68] max-w-sm mx-auto">
                {videoId
                  ? <>None of your silo files reference video <code className="bg-[#F0F1EE] px-1 rounded">{videoId}</code>. Skip, or pick any file from the silo below.</>
                  : <>This draft has no linked video. Skip, or pick any file from the silo below.</>}
              </div>
            </div>
          )}

          {!loading && matches.length > 0 && (
            <div className="space-y-2">
              <div className="text-[10.5px] uppercase tracking-[0.16em] text-[#6B6A68] mb-1">
                Pick the file this Q&amp;A belongs to
              </div>
              {matches.map((m) => (
                <label key={m.doc_id}
                  data-testid={`yt-append-file-option-${m.doc_id}`}
                  className={`block border rounded-md p-3 cursor-pointer transition-colors ${
                    selectedDoc === m.doc_id ? "bg-[#EAF3EC] border-[#2F6B3A]" : "bg-white border-[#E4E6E1] hover:bg-[#F0F1EE]/60"
                  }`}>
                  <div className="flex items-start gap-2">
                    <input
                      type="radio"
                      name="append-target"
                      value={m.doc_id}
                      checked={selectedDoc === m.doc_id}
                      onChange={() => setSelectedDoc(m.doc_id)}
                      className="mt-1"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-[12.5px] font-medium text-[#1C1B1A] flex items-center gap-1.5">
                        <FileText size={12} weight="duotone" className="text-[#4A5D4E]" />
                        <span className="truncate">{m.name}</span>
                        <span className="ml-auto text-[10px] text-[#6B6A68] uppercase tracking-[0.14em] font-normal shrink-0">Tier {m.tier}</span>
                      </div>
                      {m.snippet && (
                        <div className="text-[11px] text-[#6B6A68] mt-1 italic leading-snug break-words">{m.snippet}</div>
                      )}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}

          {/* Feb 2026 (Randy pt-yt-append-any-file) — "any file in silo" picker.
              Visible whenever we have a silo_id — even on the no-matches branch,
              because that's precisely when Randy needs an override. Collapsed by
              default so it doesn't steal focus from a correct auto-match. */}
          {!loading && siloId && (
            <div className="mt-4 border-t hairline pt-3" data-testid="yt-append-any-file-section">
              <button
                type="button"
                onClick={openAnyPicker}
                data-testid="yt-append-any-file-toggle"
                className="w-full flex items-center justify-between text-left text-[12px] font-medium text-[#1C1B1A] hover:bg-[#F0F1EE]/60 px-2 py-2 rounded-md"
              >
                <span>
                  {matches.length > 0 ? "Or append to any file in this silo…" : "Append to any file in this silo…"}
                </span>
                {anyOpen ? <CaretUp size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
              </button>
              {anyOpen && (
                <div className="mt-2 space-y-2 max-h-64 overflow-y-auto pr-1" data-testid="yt-append-any-file-list">
                  {anyLoading && (
                    <div className="text-center py-4 text-[11.5px] text-[#6B6A68]">Loading files…</div>
                  )}
                  {!anyLoading && anyDocs.length === 0 && (
                    <div className="text-center py-4 text-[11.5px] text-[#6B6A68]">This silo has no files yet.</div>
                  )}
                  {!anyLoading && anyDocs.map((d) => {
                    const _name = d.title || d.filename || d.name || "(unnamed file)";
                    const _tier = d.tier || 3;
                    return (
                      <label key={d.id}
                        data-testid={`yt-append-any-file-option-${d.id}`}
                        className={`block border rounded-md p-2.5 cursor-pointer transition-colors ${
                          selectedDoc === d.id ? "bg-[#EAF3EC] border-[#2F6B3A]" : "bg-white border-[#E4E6E1] hover:bg-[#F0F1EE]/60"
                        }`}>
                        <div className="flex items-start gap-2">
                          <input
                            type="radio"
                            name="append-target"
                            value={d.id}
                            checked={selectedDoc === d.id}
                            onChange={() => setSelectedDoc(d.id)}
                            className="mt-1"
                          />
                          <div className="min-w-0 flex-1 flex items-center gap-1.5">
                            <FileText size={12} weight="duotone" className="text-[#4A5D4E] shrink-0" />
                            <span className="truncate text-[12.5px] font-medium text-[#1C1B1A]">{_name}</span>
                            <span className="ml-auto text-[10px] text-[#6B6A68] uppercase tracking-[0.14em] font-normal shrink-0">Tier {_tier}</span>
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t hairline px-4 py-3 bg-white/40">
          <button onClick={onClose}
            data-testid="yt-append-skip"
            className="text-[12px] px-3 py-1.5 rounded-md text-[#6B6A68] hover:bg-[#F0F1EE]">
            Skip
          </button>
          {!loading && (matches.length > 0 || (siloId && anyOpen && anyDocs.length > 0)) && (
            <button onClick={doAppend} disabled={!selectedDoc || busy}
              data-testid="yt-append-confirm"
              className="text-[12px] px-4 py-1.5 rounded-md bg-[#2F6B3A] text-white hover:bg-[#245029] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5">
              <Check size={11} weight="bold" /> {busy ? "Saving…" : "Append to and save"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
